/*
  DCL variant recovery
  --------------------
  Added 2026-08-28. Fourth revision 2026-08-29, see "History" at the bottom.

  The problem, in two parts:
  Some products have option values that do not exist in every combination. The Garmin
  fenix 9 Pro is the clearest case: Garmin makes four colorways at 43 mm and a different
  three at 47 and 51 mm, with no overlap at all. So a customer looking at Shell Pink who
  clicks 47 mm lands on a combination that is not a product.

  Two things then go wrong for that customer:
    1. The page shows UNAVAILABLE with no price, which reads like the product is
       discontinued rather than like they should pick another color.
    2. Nothing on the page ever told them Shell Pink was a 43 mm colorway.

  Note this is NOT the same as sold out. A variant that exists but has no stock still
  shows its price and an Out of Stock button. That is correct and untouched here.

  What this file does:
    1. RECOVERY. On a dead combination, it finds a real variant containing the option
       value the customer just clicked, ticks the picker across to that variant's actual
       combination, and fires one change event so the theme resolves it normally. The
       customer never sees the dead state.
    2. HINT. It prints a short line under the option row saying which sizes the selected
       colorway actually comes in, so the snap is explained before it happens rather than
       feeling like the page changed its mind.

  Scope:
  The hint only renders on products whose option matrix is genuinely incomplete. On a
  product where every combination exists, nothing is added and nothing is fetched beyond
  the one cached variant list. Most of the catalog is in that group and sees no change.

  Why the variant list and not Shopify's resolver:
  Resolving from a single option value goes through selected_or_first_available_variant.
  When every variant on a product is out of stock, that returns nothing, and the recovery
  falls straight back into the dead state it was trying to escape. The .js endpoint lists
  every variant regardless of stock, so this works either way.

  History, so nobody repeats these:
  v1 patched handleOptionValueChange. Never ran: the theme subscribes with .bind(this)
     in connectedCallback, capturing the original method before this file loads.
  v2 read the clicked input from a capture-phase DOM listener, fixing that, but still
     resolved through the availability path above. Failed for that reason.
  v3 switched to the .js variant list. Confirmed working 2026-08-29.
  v4 adds the hint line and scopes it to incomplete matrices.
*/
(function () {
  if (window.dclVariantRecoveryInstalled) return;
  window.dclVariantRecoveryInstalled = true;

  var HINT_ATTR = 'data-dcl-availability-hint';
  var variantCache = {};

  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!input || typeof input.closest !== 'function') return;
      if (!input.closest('variant-selects')) return;

      var productInfo = input.closest('product-info');
      if (!productInfo) return;

      productInfo.dclLastInput = input;
      productInfo.dclRecovering = false;
    },
    true
  );

  // The picker names inputs "{option name}-{option position}", so the trailing
  // number tells us which option was clicked without guessing at labels.
  function optionIndexFromInput(input) {
    if (!input || !input.name) return -1;
    var position = parseInt(input.name.split('-').pop(), 10);
    if (isNaN(position) || position < 1) return -1;
    return position - 1;
  }

  function fetchVariants(productUrl) {
    if (variantCache[productUrl]) return variantCache[productUrl];

    variantCache[productUrl] = fetch(productUrl + '.js')
      .then(function (response) {
        if (!response.ok) throw new Error('variant fetch failed');
        return response.json();
      })
      .then(function (data) {
        return data && data.variants ? data.variants : [];
      })
      .catch(function () {
        return [];
      });

    return variantCache[productUrl];
  }

  function distinctValues(variants, index) {
    var seen = [];
    for (var i = 0; i < variants.length; i++) {
      var options = variants[i].options || [];
      var value = options[index];
      if (value && seen.indexOf(value) === -1) seen.push(value);
    }
    return seen;
  }

  // A matrix is incomplete when there are fewer variants than every combination
  // of option values would produce. Only those products get a hint.
  function matrixIsIncomplete(variants, optionCount) {
    if (!variants.length || optionCount < 2) return false;

    var expected = 1;
    for (var i = 0; i < optionCount; i++) {
      expected = expected * distinctValues(variants, i).length;
    }
    return variants.length < expected;
  }

  function selectedValues(productInfo, optionCount) {
    var values = new Array(optionCount);
    var inputs = productInfo.querySelectorAll('variant-selects input[type="radio"]:checked');

    for (var i = 0; i < inputs.length; i++) {
      var index = optionIndexFromInput(inputs[i]);
      if (index >= 0 && index < optionCount) values[index] = inputs[i].value;
    }
    return values;
  }

  function joinReadable(list) {
    if (list.length === 1) return list[0] + ' only';
    if (list.length === 2) return list[0] + ' and ' + list[1];
    return list.slice(0, -1).join(', ') + ', and ' + list[list.length - 1];
  }

  // Print which values of the FIRST option the currently selected LAST option
  // exists with, but only when that is a subset. Nothing to explain otherwise.
  function renderHint(productInfo, variants, optionCount) {
    var fieldsets = productInfo.querySelectorAll('variant-selects fieldset, variant-selects div.product-form__input');
    var lastFieldset = fieldsets[fieldsets.length - 1];
    if (!lastFieldset) return;

    var existing = lastFieldset.querySelector('[' + HINT_ATTR + ']');
    var chosen = selectedValues(productInfo, optionCount);
    var chosenLast = chosen[optionCount - 1];
    if (!chosenLast) {
      if (existing) existing.remove();
      return;
    }

    var allFirst = distinctValues(variants, 0);
    var pairedWith = [];

    for (var i = 0; i < variants.length; i++) {
      var options = variants[i].options || [];
      if (options[optionCount - 1] !== chosenLast) continue;
      if (pairedWith.indexOf(options[0]) === -1) pairedWith.push(options[0]);
    }

    if (!pairedWith.length || pairedWith.length === allFirst.length) {
      if (existing) existing.remove();
      return;
    }

    // Keep the source order of the option row, not the variant order.
    pairedWith.sort(function (a, b) {
      return allFirst.indexOf(a) - allFirst.indexOf(b);
    });

    var text = chosenLast + ' comes in ' + joinReadable(pairedWith) + '.';
    if (existing && existing.textContent === text) return;

    var node = existing || document.createElement('div');
    node.setAttribute(HINT_ATTR, '');
    node.style.width = '100%';
    node.style.marginTop = '0.5rem';
    node.style.fontSize = '0.9em';
    node.style.opacity = '0.7';
    node.textContent = text;

    if (!existing) lastFieldset.appendChild(node);
  }

  // The theme replaces the picker markup on every variant change, so the hint has
  // to be re-applied afterwards. Observe rather than guess at timing.
  function watchProduct(productInfo, variants, optionCount) {
    var scheduled = false;

    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        observer.disconnect();
        renderHint(productInfo, variants, optionCount);
        observer.observe(productInfo, { childList: true, subtree: true });
      });
    });

    renderHint(productInfo, variants, optionCount);
    observer.observe(productInfo, { childList: true, subtree: true });
  }

  function initHints() {
    var products = document.querySelectorAll('product-info[data-url]');

    for (var i = 0; i < products.length; i++) {
      (function (productInfo) {
        var fieldsets = productInfo.querySelectorAll(
          'variant-selects fieldset, variant-selects div.product-form__input'
        );
        var optionCount = fieldsets.length;
        if (optionCount < 2) return;

        fetchVariants(productInfo.dataset.url).then(function (variants) {
          if (!matrixIsIncomplete(variants, optionCount)) return;
          watchProduct(productInfo, variants, optionCount);
        });
      })(products[i]);
    }
  }

  if (window.requestIdleCallback) {
    window.requestIdleCallback(initHints);
  } else {
    window.setTimeout(initHints, 1200);
  }

  if (!window.customElements || !customElements.whenDefined) return;

  customElements.whenDefined('product-info').then(function () {
    var ProductInfo = customElements.get('product-info');
    if (!ProductInfo || !ProductInfo.prototype) return;

    var proto = ProductInfo.prototype;
    if (proto.dclRecoveryPatched) return;

    var originalSetUnavailable = proto.setUnavailable;
    if (typeof originalSetUnavailable !== 'function') return;

    proto.dclRecoveryPatched = true;

    proto.setUnavailable = function () {
      var productInfo = this;
      var input = productInfo.dclLastInput;
      var productUrl = productInfo.dataset ? productInfo.dataset.url : null;
      var optionIndex = optionIndexFromInput(input);

      // Only recover once per click, so a genuinely impossible product still
      // reaches the theme's normal unavailable state instead of looping.
      if (!input || !productUrl || optionIndex < 0 || productInfo.dclRecovering) {
        productInfo.dclRecovering = false;
        return originalSetUnavailable.call(productInfo);
      }

      productInfo.dclRecovering = true;
      var clickedValue = input.value;

      fetchVariants(productUrl).then(function (variants) {
        var match = null;

        for (var i = 0; i < variants.length; i++) {
          var options = variants[i].options || [];
          if (options[optionIndex] === clickedValue) {
            match = variants[i];
            break;
          }
        }

        if (!match || !applyCombination(productInfo, match.options)) {
          originalSetUnavailable.call(productInfo);
        }
      });
    };
  });

  // Tick every option across to the target variant's combination, then fire one
  // change event so the theme resolves it the way it would for a real click.
  function applyCombination(productInfo, optionValues) {
    var inputs = productInfo.querySelectorAll('variant-selects input[type="radio"]');
    var lastTouched = null;

    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var index = optionIndexFromInput(input);
      if (index < 0 || index >= optionValues.length) continue;

      var shouldCheck = input.value === optionValues[index];
      if (shouldCheck && !input.checked) {
        input.checked = true;
        lastTouched = input;
      } else if (shouldCheck) {
        lastTouched = lastTouched || input;
      }
    }

    if (!lastTouched) return false;
    lastTouched.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
})();
